import React, {useEffect} from "react";
import {useLocation} from "react-router-dom";

import styles from "./KnowledgeSettingsPage.module.css";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {Button, ButtonState} from "../../../elements/button/Button";
import {TabChooseButton} from "../../../elements/tabChooseButton/TabChooseButton";
import {FormName, openForm} from "../../../elements/formProvider/formSlice";
import {MenuItem, updateSelectedMenuItem} from "../../platform/platformSlice";
import {useRequestErrorToast} from "../../../../hooks/useRequestErrorToast";
import {
    fetchKnowledgeAttributes,
    fetchKnowledgeTypes,
    knowledgeAttributesSelector,
    knowledgeAttributesStatusSelector,
    knowledgeTypesSelector,
    knowledgeTypesStatusSelector
} from "../../knowledge/knowledgeDefinitionsSlice";
import {KnowledgeDefinitionKind} from "../../knowledge/knowledgeDefinitionsApi";
import {KnowledgeDefinitionTable} from "../table/KnowledgeDefinitionTable";
import {KNOWLEDGE_DEFINITION_LABELS} from "../knowledgeDefinitionLabels";

export enum KnowledgeSettingsTab {
    TYPES = "types",
    ATTRIBUTES = "attributes"
}

const TAB_DESCRIPTIONS: Record<KnowledgeSettingsTab, string> = {
    [KnowledgeSettingsTab.TYPES]: "Knowledge types group knowledge records. Aime picks the type that fits a question from its description. Built-in types cannot be changed.",
    [KnowledgeSettingsTab.ATTRIBUTES]: "Attributes tag knowledge records with a value, such as a vehicle brand. Aime filters by an attribute only when the question makes it relevant."
};

/**
 * The global catalogue of custom knowledge types and attributes (shared by every assistant,
 * franchise and location). Knowledge records pick from it on their Knowledge tab.
 */
export const KnowledgeSettingsPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const pageLocation = useLocation();
    const types = useAppSelector(knowledgeTypesSelector);
    const typesStatus = useAppSelector(knowledgeTypesStatusSelector);
    const attributes = useAppSelector(knowledgeAttributesSelector);
    const attributesStatus = useAppSelector(knowledgeAttributesStatusSelector);

    useEffect(() => {
        dispatch(updateSelectedMenuItem(MenuItem.KNOWLEDGE_SETTINGS));
        dispatch(fetchKnowledgeTypes());
        dispatch(fetchKnowledgeAttributes());
    }, [dispatch]);

    useRequestErrorToast(typesStatus, "Failed to load knowledge types");
    useRequestErrorToast(attributesStatus, "Failed to load knowledge attributes");

    const hash = pageLocation.hash.replace("#", "");
    const currentTab = hash === KnowledgeSettingsTab.ATTRIBUTES ? KnowledgeSettingsTab.ATTRIBUTES : KnowledgeSettingsTab.TYPES;
    const kind = currentTab === KnowledgeSettingsTab.ATTRIBUTES ? KnowledgeDefinitionKind.ATTRIBUTE : KnowledgeDefinitionKind.TYPE;
    const labels = KNOWLEDGE_DEFINITION_LABELS[kind];

    // Only the first load shows the loader; a refresh after a save keeps the list in place.
    const isLoading = kind === KnowledgeDefinitionKind.TYPE
        ? typesStatus === RequestStatus.PENDING && types.length === 0
        : attributesStatus === RequestStatus.PENDING && attributes.length === 0;

    const openCreate = () => dispatch(openForm({name: FormName.KNOWLEDGE_DEFINITION, formProps: {kind}}));

    return <div className={styles.wrapper}>
        <div className={styles.headerSectionWrapper}>
            <span className={styles.title}>Knowledge settings</span>
            <Button
                key={kind}
                title={`Create ${labels.singular}`}
                iconSrc={"/plusIcon.svg"}
                state={ButtonState.PRIMARY}
                handleClick={openCreate}
            />
        </div>
        <div className={styles.tabContainer}>
            <TabChooseButton
                to={`#${KnowledgeSettingsTab.TYPES}`}
                replace={true}
                imageSrc={"/knowledgeTypesIcon.svg"}
                text={"Knowledge types"}
                isActive={currentTab === KnowledgeSettingsTab.TYPES}
            />
            <TabChooseButton
                to={`#${KnowledgeSettingsTab.ATTRIBUTES}`}
                replace={true}
                imageSrc={"/knowledgeAttributesIcon.svg"}
                text={"Attributes"}
                isActive={currentTab === KnowledgeSettingsTab.ATTRIBUTES}
            />
        </div>
        <p className={styles.description}>{TAB_DESCRIPTIONS[currentTab]}</p>
        <KnowledgeDefinitionTable
            kind={kind}
            definitions={kind === KnowledgeDefinitionKind.TYPE ? types : attributes}
            isLoading={isLoading}
        />
    </div>
}
